import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { validatePassword, PasswordStrengthBar } from '../utils/passwordUtils';
import PasswordInput from '../components/Auth/PasswordInput';

type CompanyType = 'sole_trader' | 'company';
type SizeCategory = 'mikro' | 'kis' | 'kozepes' | 'nagy';

const SIZE_LABELS: Record<SizeCategory, string> = {
  mikro:   'Mikrovállalkozás (0–9 fő)',
  kis:     'Kisvállalkozás (10–49 fő)',
  kozepes: 'Középvállalkozás (50–249 fő)',
  nagy:    'Nagyvállalat (250+ fő)',
};

// Derives size category from employee count (EU SME definition)
function deriveSizeCategory(count: string): SizeCategory | '' {
  const n = parseInt(count, 10);
  if (isNaN(n) || count === '') return '';
  if (n <= 9)   return 'mikro';
  if (n <= 49)  return 'kis';
  if (n <= 249) return 'kozepes';
  return 'nagy';
}

const STEPS = ['Belépési adatok', 'Céges alapadatok', 'Székhely és levelezési cím'];

const TAGOZAT_OPTIONS = [
  'Szolgáltatási tagozat',
  'Ipari tagozat',
  'Kereskedelmi tagozat',
  'Kézműipari tagozat',
];

export default function RegisterPage() {
  const { login, refresh } = useAuth();
  const navigate  = useNavigate();

  const [step, setStep] = useState(0);

  // Step 0 — account + registry pre-check
  const [email, setEmail]               = useState('');
  const [taxId, setTaxId]               = useState('');
  const [password, setPassword]         = useState('');
  const [passwordConf, setPasswordConf] = useState('');

  // Step 1 — company basics
  const [companyName, setCompanyName]     = useState('');
  const [companyType, setCompanyType]     = useState<CompanyType>('company');
  const [employeeCount, setEmployeeCount] = useState('');
  const sizeCategory = deriveSizeCategory(employeeCount);
  const [chapter, setChapter]         = useState('');

  // Step 2 — address
  const [hqZip, setHqZip]           = useState('');
  const [hqCity, setHqCity]         = useState('');
  const [hqStreet, setHqStreet]     = useState('');
  const [mailZip, setMailZip]       = useState('');
  const [mailCity, setMailCity]     = useState('');
  const [mailStreet, setMailStreet] = useState('');
  const [mailPoBox, setMailPoBox]   = useState('');

  const [error, setError]             = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading]         = useState(false);
  // Fields pre-filled from the chamber registry (shown with a badge)
  const [prefilledFields, setPrefilledFields] = useState<Set<string>>(new Set());

  function validateStep0(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!email)                     e.email = 'Az e-mail cím megadása kötelező.';
    if (!/^\d{8}$/.test(taxId))     e.taxId = 'Az adószám pontosan 8 számjegyből áll.';
    if (!password)                  e.password = 'A jelszó megadása kötelező.';
    else { const pwErr = validatePassword(password); if (pwErr) e.password = pwErr; }
    if (password && !e.password && password !== passwordConf) e.passwordConf = 'A két jelszó nem egyezik.';
    return e;
  }

  function validateStep1(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!companyName.trim()) e.companyName = 'A vállalkozás nevének megadása kötelező.';
    if (!chapter)            e.chapter     = 'Tagozat megadása kötelező.';
    return e;
  }

  function validateStep2(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!hqZip.trim())                        e.hqZip    = 'Az irányítószám megadása kötelező.';
    else if (!/^\d{4}$/.test(hqZip.trim()))   e.hqZip    = 'Az irányítószám pontosan 4 számjegyből áll.';
    if (!hqCity.trim())   e.hqCity   = 'A város megadása kötelező.';
    if (!hqStreet.trim()) e.hqStreet = 'Az utca, házszám megadása kötelező.';
    // Mail address: optional, but if any field is filled all three must be filled
    const mailFilled  = [mailZip, mailCity, mailStreet].filter((v) => v.trim());
    const mailMissing = [
      { key: 'mailZip',    val: mailZip,    label: 'Irányítószám (levelezési cím)' },
      { key: 'mailCity',   val: mailCity,   label: 'Város (levelezési cím)' },
      { key: 'mailStreet', val: mailStreet, label: 'Utca, házszám (levelezési cím)' },
    ].filter(({ val }) => !val.trim());
    if (mailFilled.length > 0 && mailMissing.length > 0) {
      mailMissing.forEach(({ key, label }) => { e[key] = `${label} megadása kötelező, ha a levelezési cím megkezdett.`; });
    }
    // Validate mail zip format if provided
    if (mailZip.trim() && !/^\d{4}$/.test(mailZip.trim())) {
      e.mailZip = 'Az irányítószám pontosan 4 számjegyből áll.';
    }
    return e;
  }

  async function handleNext(e: FormEvent) {
    e.preventDefault();
    setError('');
    const errs = step === 0 ? validateStep0() : step === 1 ? validateStep1() : validateStep2();
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }
    setFieldErrors({});

    if (step === 0) {
      // Check registry before advancing — use returned data to pre-fill form
      setLoading(true);
      try {
        const reg = await api.auth.checkRegistry({ taxId, email, password });
        const filled = new Set<string>();
        if (reg.companyName) { setCompanyName(reg.companyName); filled.add('companyName'); }
        if (reg.hqZip)       { setHqZip(reg.hqZip);             filled.add('hqZip'); }
        if (reg.hqCity)      { setHqCity(reg.hqCity);            filled.add('hqCity'); }
        if (reg.hqStreet)    { setHqStreet(reg.hqStreet);        filled.add('hqStreet'); }
        if (reg.chapter)     { setChapter(reg.chapter);           filled.add('chapter'); }
        setPrefilledFields(filled);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Ismeretlen hiba.');
        setLoading(false);
        return;
      }
      setLoading(false);
    }

    setStep((s) => s + 1);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    const errs = validateStep2();
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }
    setFieldErrors({});
    setLoading(true);
    try {
      const res = await api.auth.register({
        email, password, taxId, companyName, companyType,
        employeeCount: employeeCount ? parseInt(employeeCount, 10) : null,
        sizeCategory: (sizeCategory as SizeCategory) || null,
        hqZip: hqZip || null, hqCity: hqCity || null, hqStreet: hqStreet || null,
        mailZip: mailZip || null, mailCity: mailCity || null,
        mailStreet: mailStreet || null, mailPoBox: mailPoBox || null,
        chapter: chapter || null,
      });
      const u = res.user;
      // Registration response now includes prequalificationStatus (e.g. 'passed' for renewal bypass).
      // Call refresh() as a safety net in case the value is missing.
      login({ ...u, roles: u.roles ?? [u.role ?? 'company_user'], prequalificationStatus: u.prequalificationStatus ?? 'none', isRenewal: u.isRenewal ?? false });
      // Always refresh so companyName (and any other /me-only fields) are populated
      await refresh();
      navigate('/prequalification', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ismeretlen hiba.');
    } finally {
      setLoading(false);
    }
  }

  const pct = Math.round(((step + 1) / STEPS.length) * 100);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--background)', display: 'flex', flexDirection: 'column' }}>

      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(135deg, var(--primary) 0%, #064944 58%, var(--secondary) 150%)',
        padding: '32px 40px', display: 'flex', alignItems: 'center', gap: 18,
      }}>
        <img src="/logo_gymsmkik.svg" style={{ height: 40, width: 'auto' }} alt="GYMSMKIK" />
        <div style={{ borderLeft: '1px solid rgba(255,255,255,.3)', paddingLeft: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#fff', lineHeight: 1.2 }}>Védjegy Digitalizációs Platform</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,.72)', marginTop: 2 }}>GYMSM KIK, Védjegy pályázati portál</div>
        </div>
      </div>

      {/* ── Card ── */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 20px' }}>
        <div style={{
          background: '#fff', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: '44px 48px',
          boxShadow: 'var(--shadow-md)', width: '100%', maxWidth: 720,
        }}>
          {/* Title */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 8 }}>
              REGISZTRÁCIÓ · {step + 1} / {STEPS.length}
            </div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--primary)' }}>{STEPS[step]}</h1>
          </div>

          {/* Progress bar */}
          <div className="progress-track" style={{ marginBottom: 28 }}>
            <div className="progress-fill" style={{ width: pct + '%', transition: 'width .3s' }} />
          </div>

          {/* Step indicators */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
            {STEPS.map((label, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{
                  height: 3, borderRadius: 999,
                  background: i <= step ? 'var(--primary)' : 'var(--border)',
                  transition: 'background .3s',
                }} />
                <div style={{ fontSize: 10, color: i <= step ? 'var(--primary)' : 'var(--muted-foreground)', fontWeight: i === step ? 700 : 400 }}>
                  {label}
                </div>
              </div>
            ))}
          </div>

          {error && (
            <div className="alert alert-destructive" style={{ marginBottom: 20 }}>
              <span>&#9888;</span><div><strong>Hiba</strong><p>{error}</p></div>
            </div>
          )}

          {/* ── Step 0: account ── */}
          {step === 0 && (
            <form onSubmit={(e) => { void handleNext(e); }} noValidate>
              <div className="field">
                <label htmlFor="email">E-mail cím <span className="req">*</span></label>
                <input id="email" type="email" autoComplete="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="pelda@ceg.hu" />
                {fieldErrors.email && <div className="error">{fieldErrors.email}</div>}
              </div>
              <div className="field">
                <label htmlFor="taxId">Adószám (első 8 jegy) <span className="req">*</span></label>
                <input id="taxId" type="text" inputMode="numeric" maxLength={8}
                  value={taxId} onChange={(e) => setTaxId(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="12345678" />
                {fieldErrors.taxId && <div className="error">{fieldErrors.taxId}</div>}
                <div className="help">A platformhoz csak kamarai tagok regisztrálhatnak.</div>
              </div>
              <div className="field">
                <label htmlFor="password">Jelszó <span className="req">*</span></label>
                <PasswordInput id="password" autoComplete="new-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="Min. 8 kar., kis/nagybetű, szám, spec." />
                <PasswordStrengthBar password={password} />
                {fieldErrors.password && <div className="error">{fieldErrors.password}</div>}
              </div>
              <div className="field" style={{ marginBottom: 28 }}>
                <label htmlFor="passwordConf">Jelszó megerősítése <span className="req">*</span></label>
                <PasswordInput id="passwordConf" autoComplete="new-password" value={passwordConf}
                  onChange={(e) => setPasswordConf(e.target.value)} placeholder="••••••••" />
                {fieldErrors.passwordConf && <div className="error">{fieldErrors.passwordConf}</div>}
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
                {loading ? 'Ellenőrzés…' : 'Tovább →'}
              </button>
            </form>
          )}

          {/* ── Step 1: company basics ── */}
          {step === 1 && (
            <form onSubmit={(e) => { void handleNext(e); }} noValidate>
              {prefilledFields.size > 0 && (
                <div style={{ marginBottom: 16, padding: '8px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 12, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>ℹ️</span>
                  A kamarai nyilvántartásból előtöltöttük az adatokat. Ellenőrizze és szükség esetén módosítsa őket.
                </div>
              )}
              <div className="field">
                <label htmlFor="companyName">
                  Vállalkozás neve <span className="req">*</span>
                  {prefilledFields.has('companyName') && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, background: '#dbeafe', color: '#1d4ed8', borderRadius: 4, padding: '1px 5px' }}>Kamara</span>}
                </label>
                <input id="companyName" type="text" autoComplete="organization"
                  value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Példa Kft."
                  style={prefilledFields.has('companyName') ? { background: '#eff6ff' } : undefined} />
                {fieldErrors.companyName && <div className="error">{fieldErrors.companyName}</div>}
              </div>
              {/* Tagozat */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--secondary-foreground)', marginBottom: 6 }}>
                  Tagozat <span style={{ color: 'var(--destructive)' }}>*</span>
                  {prefilledFields.has('chapter') && (
                    <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: 'var(--secondary)', background: 'rgba(78,192,124,.12)', padding: '2px 8px', borderRadius: 999 }}>
                      Kamara
                    </span>
                  )}
                </label>
                <select
                  value={chapter}
                  onChange={(e) => setChapter(e.target.value)}
                  disabled={prefilledFields.has('chapter')}
                  required
                  style={{
                    width: '100%', padding: '10px 14px', fontSize: 14,
                    borderRadius: 'var(--radius-sm)',
                    border: `1px solid ${fieldErrors['chapter'] ? 'var(--destructive)' : 'var(--border)'}`,
                    background: prefilledFields.has('chapter') ? 'var(--muted)' : '#fff',
                    color: 'var(--primary)',
                  }}
                >
                  <option value="">— Válasszon tagozatot —</option>
                  {TAGOZAT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                {fieldErrors['chapter'] && <div style={{ fontSize: 12, color: 'var(--destructive)', marginTop: 4 }}>{fieldErrors['chapter']}</div>}
              </div>
              <div className="field">
                <label>Vállalkozás típusa <span className="req">*</span></label>
                <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                  {([
                    { value: 'company',     label: 'Társas vállalkozás', sub: 'Kft., Bt., Zrt.' },
                    { value: 'sole_trader', label: 'Egyéni vállalkozó',  sub: 'EV, KATA' },
                  ] as { value: CompanyType; label: string; sub: string }[]).map((opt) => (
                    <label key={opt.value} style={{
                      flex: 1, border: '2px solid ' + (companyType === opt.value ? 'var(--primary)' : 'var(--border)'),
                      borderRadius: 12, padding: '12px 14px',
                      background: companyType === opt.value ? 'var(--muted)' : '#fff',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 0,
                    }}>
                      <input type="radio" name="companyType" value={opt.value}
                        checked={companyType === opt.value}
                        onChange={() => setCompanyType(opt.value)}
                        style={{ width: 'auto', accentColor: 'var(--primary)' }} />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--primary)' }}>{opt.label}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{opt.sub}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 14 }}>
                <div className="field" style={{ flex: 1 }}>
                  <label htmlFor="employeeCount">Alkalmazottak száma</label>
                  <input id="employeeCount" type="number" min={0} value={employeeCount}
                    onChange={(e) => setEmployeeCount(e.target.value)} placeholder="pl. 12" />
                </div>
                <div className="field" style={{ flex: 2, marginBottom: 28 }}>
                  <label htmlFor="sizeCategory">Méretkategória</label>
                  <input
                    id="sizeCategory"
                    type="text"
                    readOnly
                    value={sizeCategory ? SIZE_LABELS[sizeCategory] : '— adja meg az alkalmazottak számát —'}
                    style={{ background: 'var(--muted)', cursor: 'default', color: sizeCategory ? 'var(--primary)' : 'var(--muted-foreground)' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button type="button" className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setStep(0)}>
                  ← Vissza
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 2 }}>
                  Tovább →
                </button>
              </div>
            </form>
          )}

          {/* ── Step 2: address ── */}
          {step === 2 && (
            <form onSubmit={(e) => { void handleSubmit(e); }} noValidate>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--primary)', marginBottom: 12, paddingBottom: 7, borderBottom: '1px solid var(--border)' }}>
                Székhely
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div className="field" style={{ width: 140, flexShrink: 0 }}>
                  <label>
                    Irányítószám <span className="req">*</span>
                    {prefilledFields.has('hqZip') && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, background: '#dbeafe', color: '#1d4ed8', borderRadius: 4, padding: '1px 5px' }}>Kamara</span>}
                  </label>
                  <input type="text" value={hqZip} onChange={(e) => setHqZip(e.target.value)} placeholder="9021"
                    style={prefilledFields.has('hqZip') ? { background: '#eff6ff' } : undefined} />
                  {fieldErrors.hqZip && <div className="error">{fieldErrors.hqZip}</div>}
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label>
                    Város <span className="req">*</span>
                    {prefilledFields.has('hqCity') && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, background: '#dbeafe', color: '#1d4ed8', borderRadius: 4, padding: '1px 5px' }}>Kamara</span>}
                  </label>
                  <input type="text" value={hqCity} onChange={(e) => setHqCity(e.target.value)} placeholder="Győr"
                    style={prefilledFields.has('hqCity') ? { background: '#eff6ff' } : undefined} />
                  {fieldErrors.hqCity && <div className="error">{fieldErrors.hqCity}</div>}
                </div>
              </div>
              <div className="field">
                <label>
                  Utca, házszám <span className="req">*</span>
                  {prefilledFields.has('hqStreet') && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, background: '#dbeafe', color: '#1d4ed8', borderRadius: 4, padding: '1px 5px' }}>Kamara</span>}
                </label>
                <input type="text" value={hqStreet} onChange={(e) => setHqStreet(e.target.value)} placeholder="Kossuth Lajos utca 7."
                  style={prefilledFields.has('hqStreet') ? { background: '#eff6ff' } : undefined} />
                {fieldErrors.hqStreet && <div className="error">{fieldErrors.hqStreet}</div>}
              </div>

              {(() => {
                // If any mail field is started, all three become required
                const mailStarted = [mailZip, mailCity, mailStreet].some((v) => v.trim());
                const req = mailStarted ? <span className="req"> *</span> : null;
                return (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--primary)', marginBottom: 12, paddingBottom: 7, borderBottom: '1px solid var(--border)', marginTop: 8 }}>
                      Levelezési cím{' '}
                      <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--muted-foreground)', textTransform: 'none', letterSpacing: 0 }}>
                        {mailStarted ? '— ha megkezdi, minden mező kötelező' : '(opcionális)'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <div className="field" style={{ width: 140, flexShrink: 0 }}>
                        <label>Irányítószám{req}</label>
                        <input type="text" value={mailZip} onChange={(e) => setMailZip(e.target.value)} placeholder="9021" />
                        {fieldErrors.mailZip && <div className="error">{fieldErrors.mailZip}</div>}
                      </div>
                      <div className="field" style={{ flex: 1 }}>
                        <label>Város{req}</label>
                        <input type="text" value={mailCity} onChange={(e) => setMailCity(e.target.value)} placeholder="Győr" />
                        {fieldErrors.mailCity && <div className="error">{fieldErrors.mailCity}</div>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 28 }}>
                      <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                        <label>Utca, házszám{req}</label>
                        <input type="text" value={mailStreet} onChange={(e) => setMailStreet(e.target.value)} />
                        {fieldErrors.mailStreet && <div className="error">{fieldErrors.mailStreet}</div>}
                      </div>
                      <div className="field" style={{ width: 130, flexShrink: 0, marginBottom: 0 }}>
                        <label>Postafiók</label>
                        <input type="text" value={mailPoBox} onChange={(e) => setMailPoBox(e.target.value)} placeholder="Pf. 12" />
                      </div>
                    </div>
                  </>
                );
              })()}

              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button type="button" className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setStep(1)}>
                  ← Vissza
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 2 }} disabled={loading}>
                  {loading ? 'Regisztráció…' : 'Fiók létrehozása →'}
                </button>
              </div>
            </form>
          )}

          {step === 0 && (
            <div style={{ textAlign: 'center', fontSize: 14, color: 'var(--muted-foreground)', marginTop: 20 }}>
              Már van fiókja?{' '}
              <Link to="/login" style={{ color: 'var(--primary)', fontWeight: 700, textDecoration: 'none' }}>
                Bejelentkezés
              </Link>
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: '16px 40px', fontSize: 12, color: 'var(--muted-foreground)', textAlign: 'center' }}>
        &copy; {new Date().getFullYear()} GYMSMKIK &mdash; Győr-Moson-Sopron Vármegyei Kereskedelmi és Iparkamara
      </div>
    </div>
  );
}
